import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Calendar,
  Download,
  Search
} from 'lucide-react';
import { Button } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminBookings, useAdminBookingStatistics, useExportAdminBookings } from '../../services/adminBookingService';
import { formatDate, formatTime } from '../../utils/dateUtils';

const AdminBookings = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    date_from: '',
    date_to: ''
  });

  // Clean filters to remove empty values
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
  );

  // Fetch all bookings for admin view (includes statistics in response)
  const { data: bookingsResponse, isLoading, error } = useAdminBookings(cleanFilters);
  const { data: statsResponse } = useAdminBookingStatistics();

  // Prefer statistics from bookings response, fallback to stats API
  const stats = bookingsResponse?.statistics || statsResponse?.data || {};

  const allBookings = bookingsResponse?.data?.data || [];
  // Filter out pending bookings
  const bookings = allBookings.filter(booking => booking.status !== 'pending');

  // Export bookings mutation
  const exportBookingsMutation = useExportAdminBookings({
    onSuccess: () => {
      console.log('Bookings exported successfully');
    },
    onError: (error) => {
      console.error('Failed to export bookings:', error);
    }
  });

  // Get status configuration
  const getStatusConfig = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      confirmed: { color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle, label: 'Confirmed' },
      in_progress: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'In Progress' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Cancelled' },
      no_show: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'No Show' }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 12) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Define table columns - minimal details
  const columns = [
    {
      key: 'booking_reference',
      label: 'Booking #',
      sortable: true,
      width: 'w-32',
      render: (booking) => (
        <span 
          className="font-mono text-xs font-medium text-primary-600 truncate block" 
          title={`#${booking.booking_reference}`}
        >
          #{truncateText(booking.booking_reference, 12)}
        </span>
      )
    },
    {
      key: 'vendor',
      label: 'Vendor',
      sortable: true,
      width: 'w-48',
      render: (booking) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {booking.vendor?.business_name || 'Unknown Vendor'}
          </p>
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {booking.vendor_service?.service_name || 'Unknown Service'}
          </p>
        </div>
      )
    },
    {
      key: 'client',
      label: 'Client',
      sortable: true,
      width: 'w-48',
      render: (booking) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {booking.client?.name || 'Unknown Client'}
          </p>
        </div>
      )
    },
    {
      key: 'event_date',
      label: 'Date',
      sortable: true,
      width: 'w-32',
      render: (booking) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(booking.event_date, 'medium')}
            </div>
            {booking.start_time && (
              <div className="text-xs text-gray-500">
                {formatTime(booking.start_time)}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'total_amount',
      label: 'Amount',
      sortable: true,
      width: 'w-24',
      render: (booking) => {
        // Helper function to safely format currency
        const formatCurrency = (value) => {
          const num = parseFloat(value);
          if (isNaN(num) || num === null || num === undefined) {
            return '0.00';
          }
          return num.toFixed(2);
        };

        // Calculate total amount client pays (subtotal + platform_fee)
        const subtotal = parseFloat(booking.subtotal_amount || 0);
        const platformFee = parseFloat(booking.platform_fee || 0);
        const totalAmountDue = subtotal + platformFee;

        // Calculate amount paid from completed payments
        const completedPayments = booking.payments?.filter(p => p.status === 'completed') || [];
        const totalAmountPaid = completedPayments.reduce((sum, payment) => {
          return sum + parseFloat(payment.amount || 0);
        }, 0);

        // Calculate deposit amount paid (for deposit payments only)
        const depositAmountPaid = completedPayments
          .filter(p => p.payment_type === 'deposit')
          .reduce((sum, payment) => {
            return sum + parseFloat(payment.amount || 0);
          }, 0);

        // Check if only deposit is paid
        const hasDepositPayment = completedPayments.some(p => p.payment_type === 'deposit');
        const hasFullPayment = completedPayments.some(p => p.payment_type === 'full_payment');
        const isFullyPaid = totalAmountPaid >= totalAmountDue && totalAmountDue > 0;
        
        // If only deposit is paid, show deposit amount, otherwise show total due or amount paid
        const showDepositAmount = hasDepositPayment && !hasFullPayment && !isFullyPaid;
        const amountToDisplay = showDepositAmount 
          ? depositAmountPaid 
          : (completedPayments.length > 0 
              ? totalAmountPaid 
              : totalAmountDue);
        
        return (
          <div className="text-right">
            <p className="text-sm font-medium text-gray-900">
              ${formatCurrency(amountToDisplay)}
            </p>
            {showDepositAmount && (
              <p className="text-xs text-gray-500 mt-0.5">Deposit</p>
            )}
            {!showDepositAmount && completedPayments.length > 0 && !isFullyPaid && (
              <p className="text-xs text-gray-500 mt-0.5">
                of ${formatCurrency(totalAmountDue)}
              </p>
            )}
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-28',
      render: (booking) => {
        const statusConfig = getStatusConfig(booking.status);
        const StatusIcon = statusConfig.icon;
        return (
          <span className="whitespace-nowrap flex-shrink-0">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </span>
          </span>
        );
      }
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'View Details',
      icon: Eye,
      variant: 'default',
      onClick: (booking) => {
        navigate(`/admin/bookings/${booking.id}`);
      }
    }
  ];

  // Handle search
  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(booking => 
      booking.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.vendor?.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.vendor_service?.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.event_location?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      status: '',
      search: '',
      date_from: '',
      date_to: ''
    });
  };

  // Handle export
  const handleExport = () => {
    exportBookingsMutation.mutate(cleanFilters);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Bookings</h3>
          <p className="text-gray-600">Unable to load bookings data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">All Bookings</h1>
                <p className="text-gray-600">Manage all bookings across the platform</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                icon={Download}
                onClick={handleExport}
                disabled={exportBookingsMutation.isPending}
              >
                {exportBookingsMutation.isPending ? 'Exporting...' : 'Export'}
              </Button>
            </div>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
          <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-4 mb-4">
            {[
              { value: '', label: 'All', count: stats.total_bookings || 0 },
              { value: 'pending', label: 'Pending', count: stats.pending_bookings || 0 },
              { value: 'confirmed', label: 'Confirmed', count: stats.confirmed_bookings || 0 },
              { value: 'in_progress', label: 'In Progress', count: stats.in_progress_bookings || 0 },
              { value: 'completed', label: 'Completed', count: stats.completed_bookings || 0 },
              { value: 'cancelled', label: 'Cancelled', count: stats.cancelled_bookings || 0 },
              { value: 'no_show', label: 'No Show', count: stats.no_show_bookings || 0 }
            ].map((tab) => (
              <button
                key={tab.value || 'all'}
                onClick={() => handleFilterChange('status', tab.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filters.status === tab.value
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search vendor, client, service..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div>
              <DatePicker
                label="From Date"
                value={filters.date_from}
                onChange={(value) => handleFilterChange('date_from', value)}
                placeholder="Select start date"
                allowPastDates={true}
                className="w-full"
                size="md"
              />
            </div>

            <div>
              <DatePicker
                label="To Date"
                value={filters.date_to}
                onChange={(value) => handleFilterChange('date_to', value)}
                placeholder="Select end date"
                allowPastDates={true}
                minDate={filters.date_from || null}
                className="w-full"
                size="md"
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <Button
              variant="outline"
              onClick={clearAllFilters}
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg border border-gray-200">
          <DataTable
            data={bookings}
            columns={columns}
            actions={actions}
            onSearch={handleSearch}
            searchPlaceholder="Search all bookings..."
            emptyMessage="No bookings found"
            loading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminBookings;
